import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, BookOpen, BookMarked, MessageCircleHeart, NotebookPen, Flame, ArrowRight, Share2 } from "lucide-react";
import ShareVerseModal from "@/components/ShareVerseModal";

const ACTION_BASE_DELAY_MS = 200;
const ACTION_STAGGER_MS = 70;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function verseText(verse, translation) {
  if (!verse) return "";
  return translation === "niv" ? verse.niv : verse.kjv;
}

function VerseHero({ verse, translation, setTranslation, isSaved, onSave, onShare }) {
  return (
    <div className="lg:col-span-2 bg-card border border-border rounded-md p-8 sm:p-12 relative animate-fade-up" style={{ animationDelay: "80ms" }} data-testid="daily-verse-card">
      <div className="flex items-center justify-between mb-6">
        <p className="label-overline">Verse of the Day · {verse?.theme}</p>
        <div className="flex items-center gap-3">
          <div className="flex rounded-sm border border-border overflow-hidden text-xs">
            {["kjv", "niv"].map((t) => (
              <button key={t} data-testid={`translation-${t}`} onClick={() => setTranslation(t)}
                className={`px-3 py-1 uppercase tracking-wider transition-colors ${translation === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                {t === "niv" ? "NIV" : "KJV"}
              </button>
            ))}
          </div>
          <button data-testid="save-verse-button" onClick={onSave} className="text-muted-foreground hover:text-accent transition-colors">
            <Bookmark className={`w-5 h-5 ${isSaved ? "fill-accent text-accent" : ""}`} />
          </button>
          <button data-testid="share-verse-button" onClick={onShare} className="text-muted-foreground hover:text-accent transition-colors">
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>
      <blockquote className="font-scripture italic text-2xl sm:text-4xl leading-relaxed text-primary">
        "{verseText(verse, translation)}"
      </blockquote>
      <p className="mt-6 font-heading text-2xl text-foreground">{verse?.ref}</p>
    </div>
  );
}

function StreakCard({ stats }) {
  return (
    <div className="bg-primary text-primary-foreground rounded-md p-8 flex flex-col justify-between animate-fade-up" style={{ animationDelay: "160ms" }} data-testid="streak-card">
      <div>
        <p className="label-overline text-primary-foreground/60">Your journey</p>
        <div className="flex items-center gap-3 mt-6">
          <Flame className="w-8 h-8 text-accent" />
          <span className="font-heading text-6xl leading-none">{stats?.streak ?? 0}</span>
        </div>
        <p className="mt-3 text-primary-foreground/80 text-sm">
          {stats?.streak ? `Day ${stats.streak} of walking in step with the Spirit.` : "Begin a practice or journal to start your streak."}
        </p>
      </div>
      <div className="mt-8 pt-6 border-t border-primary-foreground/15 grid grid-cols-2 gap-4 text-sm">
        <div><p className="font-heading text-2xl">{stats?.practices_completed ?? 0}</p><p className="text-primary-foreground/60">Practices</p></div>
        <div><p className="font-heading text-2xl">{stats?.journals ?? 0}</p><p className="text-primary-foreground/60">Reflections</p></div>
      </div>
    </div>
  );
}

function DevotionalBanner({ reading }) {
  if (!reading) return null;
  return (
    <Link to="/devotional" data-testid="daily-devotional-section"
      className="block mt-6 bg-primary text-primary-foreground rounded-md p-8 sm:p-12 hover:bg-primary/90 transition-colors duration-300 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="label-overline text-primary-foreground/60">Today's Devotional</p>
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tight mt-2">{reading.title}</h3>
          <p className="font-scripture italic text-lg text-primary-foreground/85 mt-4 max-w-2xl">
            "{reading.verse}" — {reading.verse_ref}
          </p>
          <span className="inline-flex items-center gap-2 text-sm mt-6 text-primary-foreground/90">
            Read & reflect <ArrowRight className="w-4 h-4" />
          </span>
        </div>
        <BookOpen className="w-8 h-8 text-accent shrink-0 hidden sm:block" />
      </div>
    </Link>
  );
}

function QuickActions({ devo }) {
  const actions = [
    { to: devo ? `/practice/${devo.id}` : "/practices", icon: BookOpen, label: "Begin Attunement", sub: "Today's guided practice" },
    { to: "/guide", icon: MessageCircleHeart, label: "Talk to the Guide", sub: "Seek biblical insight" },
    { to: "/journal", icon: NotebookPen, label: "Open Journal", sub: "Record your reflections" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-6 mt-6">
      {actions.map((a, i) => (
        <Link key={a.label} to={a.to} data-testid={`action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
          className="group bg-card border border-border rounded-md p-7 hover:bg-muted/40 transition-colors duration-300 animate-fade-up"
          style={{ animationDelay: `${ACTION_BASE_DELAY_MS + i * ACTION_STAGGER_MS}ms` }}>
          <a.icon className="w-6 h-6 text-accent mb-8" />
          <div className="flex items-end justify-between">
            <div>
              <h3 className="font-heading text-2xl leading-tight text-foreground">{a.label}</h3>
              <p className="text-sm text-muted-foreground mt-1">{a.sub}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all duration-300" />
          </div>
        </Link>
      ))}
    </div>
  );
}

function ContinueReadingBanner({ position }) {
  if (!position?.slug) return null;
  return (
    <Link to={`/scripture/${position.slug}/read?chapter=${position.chapter}`} data-testid="continue-reading-banner"
      className="group flex items-center justify-between mt-6 bg-card border border-border rounded-md p-6 sm:p-7 hover:bg-muted/30 transition-colors duration-300">
      <div className="flex items-center gap-4">
        <BookMarked className="w-6 h-6 text-accent" />
        <div>
          <p className="label-overline">Continue reading</p>
          <p className="font-heading text-2xl leading-tight mt-0.5">{position.book} {position.chapter}</p>
        </div>
      </div>
      <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all duration-300" />
    </Link>
  );
}

function TodayPracticeBanner({ devo }) {
  if (!devo) return null;
  return (
    <Link to={`/practice/${devo.id}`} data-testid="today-devotional-banner"
      className="block mt-6 bg-card border border-border rounded-md p-8 sm:p-10 hover:bg-muted/30 transition-colors duration-300">
      <p className="label-overline mb-3">Today's Practice · {devo.theme}</p>
      <h3 className="font-heading text-3xl tracking-tight">{devo.title}</h3>
      <p className="font-scripture italic text-lg text-primary/80 mt-3">"{devo.verse}" — {devo.verse_ref}</p>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [verse, setVerse] = useState(null);
  const [devo, setDevo] = useState(null);
  const [reading, setReading] = useState(null);
  const [stats, setStats] = useState(null);
  const [translation, setTranslation] = useState(user?.translation || "kjv");
  const [savedRefs, setSavedRefs] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    api.get("/verse/daily").then((r) => setVerse(r.data));
    api.get("/devotional/daily").then((r) => setDevo(r.data));
    api.get("/reading/daily").then((r) => setReading(r.data));
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/verses/saved").then((r) => setSavedRefs(r.data.map((v) => v.ref)));
    api.get("/bible/position").then((r) => setPosition(r.data));
  }, []);

  const toggleSave = async () => {
    if (!verse) return;
    const { data } = await api.post("/verses/save", verse);
    setSavedRefs((p) => (data.saved ? [...p, verse.ref] : p.filter((r) => r !== verse.ref)));
    toast.success(data.saved ? "Verse saved to your collection." : "Verse removed.");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="animate-fade-up">
        <p className="label-overline mb-2">{greeting()}, {user?.name?.split(" ")[0]}</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">Attune your heart today</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-12">
        <VerseHero verse={verse} translation={translation} setTranslation={setTranslation}
          isSaved={verse && savedRefs.includes(verse.ref)} onSave={toggleSave} onShare={() => setShowShare(true)} />
        <StreakCard stats={stats} />
      </div>

      <DevotionalBanner reading={reading} />
      <ContinueReadingBanner position={position} />
      <QuickActions devo={devo} />
      <TodayPracticeBanner devo={devo} />

      {showShare && verse && (
        <ShareVerseModal verse={verse} translation={translation} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
